import { resolveConfig, ALLOWED_DOMAINS } from './config'
import { buildWidgetHTML } from './dom'
import {
  buildWidgetStyles,
  LAUNCHER_INTRO_MS,
  SIDEBAR_WIDTH_PX,
  VIEW_MODE_MORPH_EASING,
  VIEW_MODE_MORPH_MS,
} from './styles'
import { QuickHelpPanel } from './QuickHelpPanel'
import type {
  BotToParentMessage,
  BotWidgetConfig,
  BotWidgetInit,
  MiniChatWindowMessage,
  ParentToBotMessage,
  QuickHelpParams,
  SpanAgentAttributes,
  ViewMode,
  ViewModeOption,
  WidgetCorner,
} from './types'

const WIDGET_ROOT_ID = 'BotWidget'
const MAIN_IFRAME_ID = 'bot-chat-iframe'
const VIEW_MODE_STORAGE_KEY = 'botChatViewMode'
const DEFAULT_VIEW_MODE: ViewMode = 'sidebar'
// Below this viewport width the chat opens as the floating window whenever floating
// mode is enabled (sidebar only if floating is off); at or above it, both modes are
// offered (with the iframe's toggle) when both are enabled. Re-evaluated on resize.
const FLOATING_ONLY_MAX_WIDTH_PX = 1530
// Matches the CSS .show-transition durations used by setFadeVisible's callers (the
// AI-assistant card, greeting bubble, badge, and error banners) — how long to keep an
// element's `display` on after removing '.show' so its fade-out finishes before it's
// actually taken out of layout.
const FADE_DURATION_MS = 300
const LAUNCHER_POSITION_STORAGE_KEY = 'botLauncherPosition'
const WIDGET_CORNERS: WidgetCorner[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left']
// Same edge offsets .bot-widget-container's CSS default (bottom: 30px; right: 20px) uses.
const LAUNCHER_EDGE_OFFSET = { x: 20, y: 30 }
// How far the pointer must move before a press on the launcher counts as a drag
// rather than a click that opens the chat.
const DRAG_THRESHOLD_PX = 5

/** Where the launcher sits: a distance from whichever horizontal/vertical screen edges it's nearest. */
interface LauncherAnchor {
  x: number
  y: number
  anchorX: 'left' | 'right'
  anchorY: 'top' | 'bottom'
}

/**
 * Host-page loader for the AI chat widget. Runs in the parent page's own
 * DOM/JS context (never inside the chatbot iframe — the iframe can't reach
 * the parent DOM at all) and is responsible for:
 *  - injecting the closed-state launcher pills, greeting bubble, AI-assistant
 *    promo card, and the chat iframe itself
 *  - bridging postMessage traffic between the host page and the iframe
 *  - the separate Quick Help ("Tax Copilot") side-panel iframe
 *
 * Usage (script tag): `new BotWidget({ botUrl, productKey, ... })`.
 * Usage (module): `import { BotWidget } from 'chatbot-widget-loader'`.
 */
export class BotWidget {
  readonly config: BotWidgetConfig
  readonly quickHelp: QuickHelpPanel

  /** Latest tenant feature-flag status reported by the iframe via `FeatureFlagsStatus` — null until received. */
  featureFlagsStatus: { adminBot: boolean; enableCodePilotAI: boolean } | null = null

  private root: HTMLDivElement | null = null
  private launcherGroup: HTMLElement | null = null
  private launcherPrimaryBtn: HTMLElement | null = null
  private hasPlayedLauncherIntro = false
  private launcherSecondaryBtn: HTMLElement | null = null
  private chatWindow: HTMLElement | null = null
  private floatingCloseBtn: HTMLElement | null = null
  private widgetContainer: HTMLElement | null = null
  private launcherAnchor: LauncherAnchor | null = null
  // Set when a drag ends, so the click the browser fires right after the pointerup
  // doesn't also open the chat.
  private suppressLauncherClick = false
  private isValid = false
  private isLauncherManuallyHidden = false
  // The chat only opens once the iframe has logged in (AIChatLogin) and posted BotReady;
  // AuthFailed (login failed) turns it off again. Until then every open request — launcher,
  // SDK open(), OpenChatBot, mini chat — is refused; one made before BotReady is remembered
  // (pendingOpen) and carried out once the login succeeds, or dropped if it fails.
  private isAuthenticated = false
  private pendingOpen = false
  private messageListener: ((event: MessageEvent) => void) | null = null
  // viewMode is what's actually applied; preferredViewMode is what the visitor chose
  // (or the stored/default one). They differ while clampViewMode overrides the
  // preference — e.g. a narrow window forcing floating — so widening the window again
  // restores the visitor's choice instead of losing it.
  private viewMode: ViewMode = DEFAULT_VIEW_MODE
  private preferredViewMode: ViewMode = DEFAULT_VIEW_MODE
  // Last SyncViewMode payload sent, so resize events only message the iframe when
  // the mode or the toggle's availability actually changes.
  private lastViewModeSync: string | null = null
  private originalBodyPaddingRight: string | null = null
  // In-flight floating ⇄ sidebar morph (see morphViewMode), cancelled if the mode
  // flips again before it finishes.
  private viewModeMorphAnimations: Animation[] = []

  // ---- mini chat (openMiniChatWindow) ----
  private miniChatOpen = false
  private miniChatIframe: HTMLIFrameElement | null = null
  private miniChatAckHandler: ((event: MessageEvent) => void) | null = null
  private miniChatRetryTimer: ReturnType<typeof setTimeout> | null = null
  // Moving an iframe to a new DOM parent reloads it, so anything posted right after
  // openMiniChatWindow() would reach a page that's mid-reload and be lost. While that
  // reload is in flight, sendIFrameMessage() queues instead, and the queue is flushed
  // once the reloaded app posts BotReady.
  private isIframeReloading = false
  private pendingIframeMessages: ParentToBotMessage[] = []

  constructor(init: BotWidgetInit) {
    this.config = resolveConfig(init)
    this.quickHelp = new QuickHelpPanel(this.config)
    void this.init()
  }

  private async init(): Promise<void> {
    this.removeExisting()

    this.isValid = Boolean(this.config.productKey)

    this.injectStyles()
    this.createDom()
    window.focus()

    if (!this.isValid) {
      // NOTE: preserved from the source — the early return only happens when
      // there's a visible launcher button to disable. With isShowLauncherIcon
      // false, an invalid key currently falls through and still loads the
      // iframe below (letting the iframe app surface its own auth error).
      // Flagged as worth revisiting, not changed, since it's a real prod
      // behavior for tenants running without the launcher icon.
      if (this.launcherGroup && this.config.isShowLauncherIcon) {
        this.launcherGroup.classList.add('show', 'disabled')
        ;(this.launcherPrimaryBtn as HTMLButtonElement | null)?.setAttribute('disabled', 'true')
        ;(this.launcherSecondaryBtn as HTMLButtonElement | null)?.setAttribute('disabled', 'true')
        this.showError('Invalid project key. Chat bot is disabled.')
        return
      }
    }

    this.loadMainIframe()
    this.bindLauncherEvents()
    this.bindMessageBridge()
    window.addEventListener('resize', this.handleResize)
  }

  // ---- setup -------------------------------------------------------------

  private removeExisting(): void {
    document.getElementById(WIDGET_ROOT_ID)?.remove()
    document.querySelector('.bot-widget-container')?.remove()
    if (this.messageListener) window.removeEventListener('message', this.messageListener)
    window.removeEventListener('resize', this.handleResize)
    if (this.originalBodyPaddingRight !== null) {
      document.body.style.paddingRight = this.originalBodyPaddingRight
      this.originalBodyPaddingRight = null
    }
  }

  private injectStyles(): void {
    const style = document.createElement('style')
    style.textContent = buildWidgetStyles(this.config)
    document.head.appendChild(style)
  }

  private createDom(): void {
    this.root = document.createElement('div')
    this.root.id = WIDGET_ROOT_ID
    this.root.innerHTML = buildWidgetHTML(this.config)

    const mount = document.querySelector('main') ?? document.body
    mount.appendChild(this.root)

    this.launcherGroup = this.root.querySelector('.bot-launcher-group')
    this.launcherPrimaryBtn = this.root.querySelector('.bot-launcher-primary')
    this.launcherSecondaryBtn = this.root.querySelector('.bot-launcher-secondary')
    this.chatWindow = this.root.querySelector('.bot-chat-window')
    this.floatingCloseBtn = this.root.querySelector('.bot-floating-close-btn')
    this.floatingCloseBtn?.addEventListener('click', () => this.openChatWidget())
    this.widgetContainer = this.root.querySelector('.bot-widget-container')

    this.launcherAnchor =
      (this.config.isEnableLauncherDrag && this.readStoredLauncherAnchor()) ||
      this.cornerToAnchor(this.config.launcherPosition)
    this.applyLauncherAnchor(this.launcherAnchor)
    this.bindLauncherDrag()

    // chatWidgetPosition only moves the Floating Overlay Mode card, and only on home
    // sites; everywhere else it stays bottom-right (sidebar mode is always right-docked).
    const chatCorner = this.config.chatWidgetPosition
    if (
      this.config.isHomeSite &&
      chatCorner &&
      chatCorner !== 'bottom-right' &&
      WIDGET_CORNERS.includes(chatCorner)
    ) {
      this.chatWindow?.classList.add(`chat-pos-${chatCorner}`)
      this.floatingCloseBtn?.classList.add(`chat-pos-${chatCorner}`)
    }

    this.preferredViewMode = this.readStoredViewMode()
    this.viewMode = this.clampViewMode(this.preferredViewMode)
    this.chatWindow?.classList.add(this.viewModeClassName(this.viewMode))
    this.syncFloatingCloseButton()
    this.syncLauncherVisibility()

    if (this.config.greeting.enabled) this.setupGreeting()
  }

  private loadMainIframe(): void {
    const iframe = this.root?.querySelector<HTMLIFrameElement>('.bot-chat-iframe')
    if (!iframe) return

    const store = JSON.parse(localStorage.getItem('myStore') || localStorage.getItem('userInfo') || 'null')
    const jwt = store?.session?.JwtToken || this.config.JwtToken

    const userDetail = {
      EmailAddress: this.config.emailAddress,
      ContactName: this.config.contactName,
      Product: this.config.product,
      ProductId: this.config.productId,
      PageId: this.config.pageId,
      ProductKey: this.config.productKey,
      PartnerName: this.config.partnerName,
      PageUrl: location.href,
      IsHomeSite: this.config.isHomeSite,
      JwtToken: jwt,
      SpanAgentAttributes: this.config.spanAgentAttributes,
    }

    iframe.setAttribute('importance', 'high')
    iframe.setAttribute('loading', 'eager')
    iframe.setAttribute('fetchpriority', 'high')
    iframe.src = `${this.config.botUrl}?UserDetail=${window.btoa(JSON.stringify(userDetail))}`

    // Cache the iframe reference and mark when it's loaded — openMiniChatWindow()
    // reads both (the cache so it still finds the iframe after it's been
    // reparented into a host-page slot, where the .bot-chat-iframe/#bot-chat-iframe
    // selectors below no longer apply; the marker so it knows whether it's safe
    // to postMessage immediately or must wait for 'load' first).
    this.miniChatIframe = iframe
    iframe.addEventListener('load', () => {
      iframe.setAttribute('data-loaded', 'true')
    })
  }

  private bindLauncherEvents(): void {
    const tryItNowButton = this.root?.querySelector('.ai-try-now-btn')
    const aiAssistantCard = this.root?.querySelector<HTMLElement>('.ai-assistant-card')
    const aiCloseBtn = this.root?.querySelector('.ai-close-icon')

    // Both launcher pills open the chat in its current view mode (sidebar by
    // default — see clampViewMode) — "Try another version" has no distinct
    // behavior specified, so it does the same thing as the primary pill.
    const openFromLauncher = () => {
      if (this.suppressLauncherClick) {
        this.suppressLauncherClick = false
        return
      }
      if (!this.chatWindow?.classList.contains('open')) this.openChatWidget()
      if (this.config.aiAssistant.isShow) this.dismissAiAssistantCard(aiAssistantCard)
    }
    this.launcherPrimaryBtn?.addEventListener('click', openFromLauncher)
    this.launcherSecondaryBtn?.addEventListener('click', openFromLauncher)

    tryItNowButton?.addEventListener('click', () => {
      this.openChatWidget()
      this.dismissAiAssistantCard(aiAssistantCard)
    })
    aiCloseBtn?.addEventListener('click', () => this.dismissAiAssistantCard(aiAssistantCard))
  }

  private bindMessageBridge(): void {
    this.messageListener = (event: MessageEvent) => {
      if (event.origin !== new URL(this.config.botUrl).origin) return
      const message = JSON.parse(event.data) as BotToParentMessage
      this.handleBotMessage(message)
    }
    window.addEventListener('message', this.messageListener)
  }

  // ---- outbound (parent -> iframe) ---------------------------------------

  sendIFrameMessage(message: ParentToBotMessage): void {
    // The cached reference first: openMiniChatWindow() strips MAIN_IFRAME_ID while the
    // iframe sits in a host-page slot, so the id lookup alone finds nothing then.
    const data = JSON.stringify(message)
    if (this.isIframeReloading) {
      // Identical repeats (e.g. the same form clicked several times during the
      // reload) collapse into one, so the reloaded chat only gets asked once.
      if (!this.pendingIframeMessages.some((pending) => JSON.stringify(pending) === data)) {
        this.pendingIframeMessages.push(message)
      }
      return
    }
    const iframe =
      this.miniChatIframe ?? (document.getElementById(MAIN_IFRAME_ID) as HTMLIFrameElement | null)
    iframe?.contentWindow?.postMessage(data, this.config.botUrl)
  }

  private flushIframeMessages(): void {
    this.isIframeReloading = false
    const pending = this.pendingIframeMessages
    this.pendingIframeMessages = []
    pending.forEach((message) => this.sendIFrameMessage(message))
  }

  openChatWidget(): void {
    const isOpening = !this.chatWindow?.classList.contains('open')
    if (isOpening && !this.isAuthenticated) {
      this.pendingOpen = true
      return
    }
    this.chatWindow?.classList.toggle('open')
    this.syncBodyPadding()
    this.syncFloatingCloseButton()
    this.syncLauncherVisibility()
    this.setFadeVisible(this.root?.querySelector<HTMLElement>('.bot-badge'), false, 'flex')
    this.root?.querySelector('.application-error-ans')?.classList.remove('show')
    this.setFadeVisible(this.root?.querySelector<HTMLElement>('.bot-greeting-widget'), false, 'block')
    this.sendIFrameMessage({ type: 'ClearNotificationCount' })
    if (sessionStorage.getItem('CodePilotAI') === 'true') {
      // Same reasoning as the FeatureFlagsStatus handler above — a stored 'floating'
      // preference from an earlier visit (this tab, or another product on the same
      // origin) shouldn't win once we already know this session is CodePilotAI.
      if (this.viewMode !== 'sidebar') this.setViewMode('sidebar')
    }
  }

  /** Opens the separate Quick Help ("Tax Copilot") panel — a second, independent iframe. */
  openQuickHelp(params: QuickHelpParams = {}): void {
    this.quickHelp.updateConfig(this.config)
    this.quickHelp.open(params)
  }

  /**
   * Ported from the original AIChatBotWidget.js. Moves the already-loaded main
   * iframe out of its floating/sidebar chat window and into a host-page DOM
   * slot ({type: 'open', targetSlot}), or back into the chat window
   * ({type: 'close'}) — for embedding the chat inline elsewhere on the page
   * instead of its own floating/docked window. Confirms the switch actually
   * reached the iframe via its own ack/retry handshake — deliberately
   * independent of the regular sendIFrameMessage/BotToParentMessage bridge —
   * since a dropped postMessage here would leave the iframe showing the wrong
   * mode with no other signal to recover from.
   */
  openMiniChatWindow(message: MiniChatWindowMessage | null | undefined): void {
    if (!message) return

    if (!this.miniChatIframe) {
      this.miniChatIframe = document.getElementById(MAIN_IFRAME_ID) as HTMLIFrameElement | null
    }

    const iframe = this.miniChatIframe
    if (!iframe) return

    const isOpen = message.type === 'open'
    // Same rule as openChatWidget(): no chat without a successful login.
    if (isOpen && !this.isAuthenticated) return

    if (isOpen) {
      const targetSlot = message.targetSlot
        ? typeof message.targetSlot === 'string'
          ? document.getElementById(message.targetSlot)
          : message.targetSlot
        : null

      if (!targetSlot || this.miniChatOpen) return

      this.miniChatOpen = true
      this.isIframeReloading = true
      iframe.classList.remove('bot-chat-iframe')
      iframe.removeAttribute('id')
      targetSlot.appendChild(iframe)

      if (this.chatWindow) {
        this.chatWindow.classList.remove('open')
        this.chatWindow.style.visibility = 'hidden'
      }
    } else {
      if (!this.chatWindow || !this.miniChatOpen) return

      this.miniChatOpen = false
      this.isIframeReloading = true
      iframe.classList.add('bot-chat-iframe')
      iframe.setAttribute('id', MAIN_IFRAME_ID)
      this.chatWindow.appendChild(iframe)
      this.chatWindow.style.visibility = ''
    }

    const data = JSON.stringify({ type: 'miniChat', mode: isOpen ? 'dialog' : 'float' })

    // Clear any previous retry/ack cycle still in flight before starting a new one.
    if (this.miniChatAckHandler) {
      window.removeEventListener('message', this.miniChatAckHandler)
      this.miniChatAckHandler = null
    }
    if (this.miniChatRetryTimer) {
      clearTimeout(this.miniChatRetryTimer)
      this.miniChatRetryTimer = null
    }

    let ackReceived = false

    this.miniChatAckHandler = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg?.type === 'miniChatAck') {
          ackReceived = true
          if (this.miniChatAckHandler) window.removeEventListener('message', this.miniChatAckHandler)
          this.miniChatAckHandler = null
          if (this.miniChatRetryTimer) clearTimeout(this.miniChatRetryTimer)
          this.miniChatRetryTimer = null
        }
      } catch {
        // ignore unrelated / non-JSON postMessage traffic
      }
    }
    window.addEventListener('message', this.miniChatAckHandler)

    const sendWithRetry = (retries = 10, delay = 500): void => {
      if (ackReceived) return
      if (!iframe.contentWindow) {
        if (retries > 0) this.miniChatRetryTimer = setTimeout(() => sendWithRetry(retries - 1, delay), delay)
        return
      }
      iframe.contentWindow.postMessage(data, this.config.botUrl)
      if (retries > 0) this.miniChatRetryTimer = setTimeout(() => sendWithRetry(retries - 1, delay), delay)
    }

    const isLoaded = iframe.getAttribute('data-loaded') === 'true'
    if (isLoaded) {
      sendWithRetry()
    } else {
      iframe.addEventListener(
        'load',
        () => {
          iframe.setAttribute('data-loaded', 'true')
          sendWithRetry()
        },
        { once: true }
      )
    }
  }

  /**
   * Pushes an updated pageId into the already-loaded iframe via postMessage, instead
   * of the host page having to reinstantiate BotWidget (which reloads the whole
   * iframe) just to change pageId on its own navigation. `this.config` is only a
   * readonly *binding* — mutating its `pageId` field keeps it consistent with what
   * the widget now actually holds.
   */
  updatePageContext(pageId: string): void {
    this.config.pageId = pageId
    this.sendIFrameMessage({ type: 'UpdatePageContext', pageId })
  }

  /**
   * Merges updated SPAN AGENT attributes into the already-loaded iframe (same no-reload
   * reasoning as updatePageContext). Merged, not replaced, on both ends — a host page can
   * send just the field that changed (e.g. `{ form_id: 15 }`); set a key to null to clear it.
   */
  updateSpanAgentAttributes(attributes: SpanAgentAttributes): void {
    this.config.spanAgentAttributes = { ...this.config.spanAgentAttributes, ...attributes }
    this.sendIFrameMessage({ type: 'SpanAgentAttributes', attributes })
  }

  /** Idempotent open — unlike openChatWidget()'s toggle, safe to call repeatedly. */
  open(): void {
    if (!this.chatWindow?.classList.contains('open')) this.openChatWidget()
  }

  /** Idempotent close — the toggle counterpart to open(). */
  close(): void {
    if (this.chatWindow?.classList.contains('open')) this.openChatWidget()
  }

  /** Removes the widget (and Quick Help panel) from the DOM and detaches listeners. */
  destroy(): void {
    this.quickHelp.close()
    this.removeExisting()
  }

  // ---- inbound (iframe -> parent) ----------------------------------------

  private handleBotMessage(message: BotToParentMessage): void {
    const count = message.count ?? 1
    switch (message.command) {
      case 'BotReady':
        // A reload (see isIframeReloading) that outlasts openMiniChatWindow()'s
        // retry window would otherwise leave the fresh app not knowing it's in mini mode.
        if (this.miniChatOpen) {
          this.miniChatIframe?.contentWindow?.postMessage(
            JSON.stringify({ type: 'miniChat', mode: 'dialog' }),
            this.config.botUrl
          )
        }
        this.flushIframeMessages()
        this.isAuthenticated = true
        this.initBot(message.HasSeenAIAssistant ?? false)
        if (this.pendingOpen) {
          this.pendingOpen = false
          this.open()
        }
        break
      case 'AuthFailed':
        this.handleAuthFailed()
        break
      case 'MaxmizeChat':
        this.chatWindow?.classList.remove('maxmimumWidth')
        break
      case 'IncreaseMaxmizeChat':
        // Floating window only — a docked sidebar is never maximized.
        if (this.viewMode === 'floating' && this.canExpandWidget())
          this.chatWindow?.classList.add('maxmimumWidth')
        break
      case 'MinimizeChat':
        this.chatWindow?.classList.remove('maxmimumWidth')
        this.chatWindow?.classList.toggle('open')
        this.syncBodyPadding()
        this.syncFloatingCloseButton()
        this.syncLauncherVisibility()
        break
      case 'ResponseReceived':
        this.updateBadgeCount(count, message.text)
        break
      case 'Notification':
        this.updateBadgeCount(count)
        break
      case 'UnAuthorized':
        this.addFrameAncestors()
        break
      case 'OpenChatBot':
        // The mini chat already shows the iframe inline on the host page — don't
        // also pop the floating/docked window open behind it (e.g. on the
        // OpenAIChat a mini chat form question sends).
        if (!this.miniChatOpen) this.open()
        break
      case 'MinimizeChatWidget':
        this.chatWindow?.classList.remove('maxmimumWidth')
        break
      case 'CaptureSnap':
        this.captureDom()
        break
      case 'CodePilotAIBot':
        sessionStorage.setItem('CodePilotAI', 'true')
        break
      case 'FeatureFlagsStatus':
        this.featureFlagsStatus = {
          adminBot: Boolean(message.adminBot),
          enableCodePilotAI: Boolean(message.enableCodePilotAI),
        }
        // CodePilotAI is sidebar-only (no floating mode at all — see clampViewMode
        // below), but this only arrives after createDom() already applied whatever
        // readStoredViewMode() found in localStorage — a preference from a previous
        // visit, possibly to a different, non-CodePilotAI product on this same host
        // origin. Correct it now rather than leaving CodePilotAI stuck floating
        // until the visitor happens to toggle it themselves.
        if (this.featureFlagsStatus.enableCodePilotAI && this.viewMode !== 'sidebar') {
          this.setViewMode('sidebar')
        }
        break
      case 'HideChatBot':
        this.toggleBotIconVisible(false)
        break
      case 'ShowChatBot':
        this.toggleBotIconVisible(true)
        break
      case 'ToggleFloatingChat': {
        const requestedFloating = message.isFloating ?? this.viewMode !== 'floating'
        this.setViewMode(requestedFloating ? 'floating' : 'sidebar', true)
        break
      }
    }
  }

  // ---- view mode (Floating Overlay vs Sidebar Panel) ---------------------

  // New (not in the original) — the iframe can't resize or reposition its own host
  // frame, so the header's toggle posts ToggleFloatingChat and the actual layout
  // change happens here, on the parent page. The mode is persisted to localStorage
  // (keyed per host page's origin, like the rest of this widget's local storage
  // use) so it survives both SPA navigation and full page reloads.
  // `persist` is only for the visitor's own choice (the iframe's toggle) — automatic
  // corrections (CodePilotAI) shouldn't overwrite their stored preference.
  private setViewMode(mode: ViewMode, persist = false): void {
    this.preferredViewMode = mode
    if (persist) {
      try {
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
      } catch {
        // localStorage can throw (private browsing, storage disabled) — the mode
        // still applies for this session, it just won't persist across reloads.
      }
    }
    this.applyViewMode()
  }

  // Re-resolves the applied mode from the preference against the current rules
  // (including viewport width) — called on every preference change and on resize.
  private applyViewMode(): void {
    if (!this.chatWindow) return
    const resolved = this.clampViewMode(this.preferredViewMode)
    if (resolved !== this.viewMode) {
      const chatWindow = this.chatWindow
      this.morphViewMode(() => {
        chatWindow.classList.remove(this.viewModeClassName(this.viewMode))
        this.viewMode = resolved
        chatWindow.classList.add(this.viewModeClassName(resolved))
        // A docked sidebar is never maximized.
        if (resolved === 'sidebar') chatWindow.classList.remove('maxmimumWidth')
      })
      this.syncBodyPadding()
      this.syncFloatingCloseButton()
    }
    // Sent even when the mode itself didn't change: crossing the width breakpoint
    // can still change whether the iframe's toggle should show.
    this.syncViewModeToIframe()
  }

  // Runs `swapClasses` (the floating-mode ⇄ sidebar-mode class change) and, if the
  // window is open, animates it from its old box to its new one instead of letting
  // it jump. A plain CSS transition can't do this: floating anchors bottom/right
  // with top: auto, the sidebar anchors top: 0, and `auto` doesn't interpolate.
  // So measure the box before and after (FLIP-style), then animate explicit pixel
  // geometry between the two with the Web Animations API — real width/height, not
  // a scale transform, so the iframe's content reflows rather than stretching.
  private morphViewMode(swapClasses: () => void): void {
    const chatWindow = this.chatWindow
    const iframe = chatWindow?.querySelector<HTMLElement>('.bot-chat-iframe')
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const canAnimate =
      chatWindow && chatWindow.classList.contains('open') && typeof chatWindow.animate === 'function'

    if (!chatWindow || !canAnimate || prefersReducedMotion) {
      swapClasses()
      return
    }

    // Measured before cancelling, so a mode flipped again mid-morph continues from
    // wherever the window currently is rather than snapping back first.
    const from = this.captureMorphFrame(chatWindow, iframe)
    this.viewModeMorphAnimations.forEach((animation) => animation.cancel())
    this.viewModeMorphAnimations = []

    chatWindow.classList.add('view-morphing')
    swapClasses()
    const to = this.captureMorphFrame(chatWindow, iframe)

    const timing: KeyframeAnimationOptions = { duration: VIEW_MODE_MORPH_MS, easing: VIEW_MODE_MORPH_EASING }
    // right/bottom/max-*/min-height are released for the duration so top/left/
    // width/height alone fully describe the box in both keyframes.
    const box = { right: 'auto', bottom: 'auto', maxWidth: 'none', maxHeight: 'none', minHeight: '0px' }
    const windowAnimation = chatWindow.animate(
      [
        { ...box, ...from.rect, borderRadius: from.radius, boxShadow: from.shadow },
        { ...box, ...to.rect, borderRadius: to.radius, boxShadow: to.shadow },
      ],
      timing
    )
    this.viewModeMorphAnimations = [windowAnimation]
    if (iframe) {
      this.viewModeMorphAnimations.push(
        iframe.animate([{ borderRadius: from.iframeRadius }, { borderRadius: to.iframeRadius }], timing)
      )
    }

    windowAnimation.onfinish = () => {
      this.viewModeMorphAnimations = []
      chatWindow.classList.remove('view-morphing')
    }
    // A cancel only ever comes from a newer morph, which takes over the class itself.
  }

  private captureMorphFrame(
    chatWindow: HTMLElement,
    iframe: HTMLElement | null | undefined
  ): {
    rect: Record<'top' | 'left' | 'width' | 'height', string>
    radius: string
    shadow: string
    iframeRadius: string
  } {
    const { top, left, width, height } = chatWindow.getBoundingClientRect()
    const style = getComputedStyle(chatWindow)
    return {
      rect: { top: `${top}px`, left: `${left}px`, width: `${width}px`, height: `${height}px` },
      radius: style.borderRadius,
      shadow: style.boxShadow,
      iframeRadius: iframe ? getComputedStyle(iframe).borderRadius : '0px',
    }
  }

  private syncViewModeToIframe(force = false): void {
    const message: ParentToBotMessage = {
      type: 'SyncViewMode',
      isFloating: this.viewMode === 'floating',
      // The iframe only shows its sidebar ⇄ floating toggle when both modes are
      // actually reachable right now — see canToggleViewMode.
      canDockSidebar: this.canToggleViewMode(),
      // The iframe hides its Expand Widget button when expanding wouldn't help.
      canExpand: this.canExpandWidget(),
    }
    const key = JSON.stringify(message)
    if (!force && key === this.lastViewModeSync) return
    this.lastViewModeSync = key
    this.sendIFrameMessage(message)
  }

  // Expanding only helps when the viewport fits config.maximumWidth plus the
  // floating window's 20px gutters on both sides — below that the window is
  // already as wide as the screen allows.
  private canExpandWidget(): boolean {
    const maximumWidth = Number(this.config.maximumWidth) || 0
    return window.innerWidth >= maximumWidth + 40
  }

  private isNarrowViewport(): boolean {
    return window.innerWidth < FLOATING_ONLY_MAX_WIDTH_PX
  }

  // Both modes enabled for this page AND the viewport is wide enough to offer the choice.
  private canToggleViewMode(): boolean {
    return this.canDockSidebar() && this.allowedViewModes() === 'both' && !this.isNarrowViewport()
  }

  // Sidebar Panel Mode is never offered on the home site — there the chat is
  // always the floating window.
  private canDockSidebar(): boolean {
    return !this.config.isHomeSite
  }

  // The configured viewMode, else the legacy isEnableFloatingMode flag
  // (true → both modes, false → sidebar only).
  private allowedViewModes(): ViewModeOption {
    return this.config.viewMode ?? (this.config.isEnableFloatingMode ? 'both' : 'sidebar')
  }

  // Re-clamp so a launcher placed near an edge can't end up off-screen after the window
  // shrinks, and re-resolve the view mode in case the width crossed the breakpoint.
  private readonly handleResize = (): void => {
    if (this.launcherAnchor) this.applyLauncherAnchor(this.launcherAnchor)
    // Shrunk below the expand breakpoint while expanded — drop back to normal size
    // (the iframe resets its own maximized state from the SyncViewMode below).
    if (!this.canExpandWidget()) this.chatWindow?.classList.remove('maxmimumWidth')
    this.applyViewMode()
  }

  // Single choke point for which view mode is actually allowed — every caller
  // (launcher, the iframe's ToggleFloatingChat, a stored preference, CodePilotAI's
  // FeatureFlagsStatus correction, window resize) routes through here:
  //  - CodePilotAI is sidebar-only.
  //  - No room to dock (see canDockSidebar) → always floating.
  //  - Only one mode allowed (see allowedViewModes) → that mode.
  //  - Viewport narrower than FLOATING_ONLY_MAX_WIDTH_PX → floating.
  //  - Otherwise whatever was asked for.
  private clampViewMode(mode: ViewMode): ViewMode {
    if (this.featureFlagsStatus?.enableCodePilotAI) return 'sidebar'
    if (!this.canDockSidebar()) return 'floating'
    const allowed = this.allowedViewModes()
    if (allowed !== 'both') return allowed
    if (this.isNarrowViewport()) return 'floating'
    return mode
  }

  // Sidebar Panel Mode pushes the host page's own content out of the way (padding
  // the body) rather than overlapping it, like a real docked panel — only while
  // actually open, and only in sidebar mode (Floating Overlay sits on top of the
  // page like any other chat bubble, so it never touches body padding). Remembers
  // whatever padding-right the host page already had so it's restored exactly,
  // not just zeroed, when the panel closes or switches to floating.
  private syncBodyPadding(): void {
    const shouldPad = this.viewMode === 'sidebar' && Boolean(this.chatWindow?.classList.contains('open'))
    if (shouldPad) {
      if (this.originalBodyPaddingRight === null) {
        this.originalBodyPaddingRight = document.body.style.paddingRight
      }
      // Same timing as the floating ⇄ sidebar morph, so the page slides aside in
      // step with the panel docking (and back as it pops out).
      document.body.style.transition = `padding-right ${VIEW_MODE_MORPH_MS}ms ${VIEW_MODE_MORPH_EASING}`
      document.body.style.paddingRight = `${SIDEBAR_WIDTH_PX}px`
    } else if (this.originalBodyPaddingRight !== null) {
      document.body.style.paddingRight = this.originalBodyPaddingRight
      this.originalBodyPaddingRight = null
    }
  }

  // The header has no close (X) icon in Floating Overlay Mode — this pill is the
  // only way to close it there — and Sidebar Panel Mode hides it entirely (closing
  // that one happens via the launcher toggle instead, same as before).
  private syncFloatingCloseButton(): void {
    const shouldShow = this.viewMode === 'floating' && Boolean(this.chatWindow?.classList.contains('open'))
    this.floatingCloseBtn?.classList.toggle('show', shouldShow)
  }

  // ---- launcher (closed-state pill group) ---------------------------------

  // New (not in the original) — replaces the old single circular toggle button.
  // Shown only when the chat window is closed, the widget hasn't been explicitly
  // hidden via HideChatBot, and isShowLauncherIcon allows it at all.
  private syncLauncherVisibility(): void {
    const shouldShow =
      this.isValid &&
      this.isAuthenticated &&
      this.config.isShowLauncherIcon &&
      !this.isLauncherManuallyHidden &&
      !this.chatWindow?.classList.contains('open')
    this.launcherGroup?.classList.toggle('show', shouldShow)

    // The pill's intro (gradient ring, sparkle twinkle, shine — see .intro in styles.ts) plays
    // only the first time it appears on this page load, not every time the chat
    // window closes and brings it back.
    if (shouldShow && !this.hasPlayedLauncherIntro && this.launcherPrimaryBtn) {
      this.hasPlayedLauncherIntro = true
      const button = this.launcherPrimaryBtn
      button.classList.add('intro')
      setTimeout(() => button.classList.remove('intro'), LAUNCHER_INTRO_MS)
    }
  }

  // ---- launcher position + drag ------------------------------------------

  private cornerToAnchor(corner: WidgetCorner | undefined): LauncherAnchor {
    const resolved = corner && WIDGET_CORNERS.includes(corner) ? corner : 'bottom-right'
    const [anchorY, anchorX] = resolved.split('-') as [LauncherAnchor['anchorY'], LauncherAnchor['anchorX']]
    return { anchorX, anchorY, x: LAUNCHER_EDGE_OFFSET.x, y: LAUNCHER_EDGE_OFFSET.y }
  }

  // Pins .bot-widget-container by its offset from the anchored edges (not always
  // top/left) so it keeps its corner when the greeting/AI-assistant cards grow the
  // stack, or the window resizes. Offsets are clamped to keep the whole stack on screen.
  private applyLauncherAnchor(anchor: LauncherAnchor): void {
    const container = this.widgetContainer
    if (!container) return
    container.dataset.anchorX = anchor.anchorX
    container.dataset.anchorY = anchor.anchorY
    const { width, height } = container.getBoundingClientRect()
    const x = Math.max(0, Math.min(anchor.x, window.innerWidth - width))
    const y = Math.max(0, Math.min(anchor.y, window.innerHeight - height))
    container.style.left = anchor.anchorX === 'left' ? `${x}px` : 'auto'
    container.style.right = anchor.anchorX === 'right' ? `${x}px` : 'auto'
    container.style.top = anchor.anchorY === 'top' ? `${y}px` : 'auto'
    container.style.bottom = anchor.anchorY === 'bottom' ? `${y}px` : 'auto'
  }

  // The launcher pills are the drag handle. A press that moves less than
  // DRAG_THRESHOLD_PX is left alone so it still clicks through and opens the chat.
  private bindLauncherDrag(): void {
    const container = this.widgetContainer
    const handle = this.launcherGroup
    if (!container || !handle || !this.config.isEnableLauncherDrag) return

    handle.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || handle.classList.contains('disabled')) return
      const start = handle.getBoundingClientRect()
      let isDragging = false

      const onMove = (moveEvent: PointerEvent) => {
        const dx = moveEvent.clientX - event.clientX
        const dy = moveEvent.clientY - event.clientY
        if (!isDragging) {
          if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return
          isDragging = true
          // Keeps pointer events coming even while over iframes elsewhere on the host page.
          handle.setPointerCapture(event.pointerId)
          container.classList.add('dragging')
        }
        // The launcher itself follows the pointer, and the greeting/AI-assistant cards
        // re-align around it live: to its left or right edge, and above or below it,
        // depending on which half of the screen it's in (the same data-anchor-* switch
        // applyLauncherAnchor uses).
        const launcherLeft = start.left + dx
        const launcherTop = start.top + dy
        container.dataset.anchorX = launcherLeft + start.width / 2 < window.innerWidth / 2 ? 'left' : 'right'
        container.dataset.anchorY = launcherTop + start.height / 2 < window.innerHeight / 2 ? 'top' : 'bottom'
        // Re-measure after that switch, since it can move the launcher within the stack.
        const stack = container.getBoundingClientRect()
        const launcher = handle.getBoundingClientRect()
        const left = launcherLeft - (launcher.left - stack.left)
        const top = launcherTop - (launcher.top - stack.top)
        container.style.left = `${Math.max(0, Math.min(left, window.innerWidth - stack.width))}px`
        container.style.top = `${Math.max(0, Math.min(top, window.innerHeight - stack.height))}px`
        container.style.right = 'auto'
        container.style.bottom = 'auto'
      }

      const onEnd = () => {
        handle.removeEventListener('pointermove', onMove)
        handle.removeEventListener('pointerup', onEnd)
        handle.removeEventListener('pointercancel', onEnd)
        if (!isDragging) return
        container.classList.remove('dragging')
        this.suppressLauncherClick = true
        // No click follows if the pointer was released off the launcher, so don't let
        // the flag linger and swallow the visitor's next real click.
        setTimeout(() => (this.suppressLauncherClick = false), 0)
        this.dropLauncher()
      }

      handle.addEventListener('pointermove', onMove)
      handle.addEventListener('pointerup', onEnd)
      handle.addEventListener('pointercancel', onEnd)
    })
  }

  // Converts the drop spot into offsets from the edges the drag already anchored to
  // (see onMove in bindLauncherDrag), so it keeps its corner from now on, and
  // remembers it.
  private dropLauncher(): void {
    const container = this.widgetContainer
    if (!container) return
    const rect = container.getBoundingClientRect()
    const anchorX = container.dataset.anchorX === 'left' ? 'left' : 'right'
    const anchorY = container.dataset.anchorY === 'top' ? 'top' : 'bottom'
    const anchor: LauncherAnchor = {
      anchorX,
      anchorY,
      x: anchorX === 'left' ? rect.left : window.innerWidth - rect.right,
      y: anchorY === 'top' ? rect.top : window.innerHeight - rect.bottom,
    }
    this.applyLauncherAnchor(anchor)
    this.launcherAnchor = anchor
    try {
      localStorage.setItem(LAUNCHER_POSITION_STORAGE_KEY, JSON.stringify(anchor))
    } catch {
      // localStorage can throw (private browsing, storage disabled): the new spot
      // still applies for this page view, it just won't be restored next load.
    }
  }

  private readStoredLauncherAnchor(): LauncherAnchor | null {
    try {
      const stored = JSON.parse(localStorage.getItem(LAUNCHER_POSITION_STORAGE_KEY) || 'null')
      if (
        (stored?.anchorX === 'left' || stored?.anchorX === 'right') &&
        (stored?.anchorY === 'top' || stored?.anchorY === 'bottom') &&
        Number.isFinite(stored.x) &&
        Number.isFinite(stored.y)
      ) {
        return stored
      }
    } catch {
      // Corrupt entry or no localStorage: fall back to config.launcherPosition.
    }
    return null
  }

  // The visitor's last chosen mode, else the sidebar default — unclamped, since it's
  // kept as preferredViewMode; clampViewMode decides what's actually applied.
  private readStoredViewMode(): ViewMode {
    let stored: string | null = null
    try {
      stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY)
    } catch {
      // localStorage can throw (private browsing, storage disabled) — use the default.
    }
    return stored === 'floating' || stored === 'sidebar' ? stored : DEFAULT_VIEW_MODE
  }

  private viewModeClassName(mode: ViewMode): string {
    return mode === 'floating' ? 'floating-mode' : 'sidebar-mode'
  }

  private initBot(hasSeenAiAssistant: boolean): void {
    this.syncLauncherVisibility()

    // The class was already applied at createDom() time (before the iframe even
    // started loading), but the iframe's own header only learns the current mode
    // once it's ready to receive messages — tell it now.
    this.syncViewModeToIframe(true)

    const shownCount = Number(localStorage.getItem('AIAssistantShown') || 0)
    const aiAssistantCard = this.root?.querySelector<HTMLElement>('.ai-assistant-card')
    const canShowAiAssistant =
      !hasSeenAiAssistant &&
      shownCount < this.config.aiAssistant.maxShowCount &&
      this.config.aiAssistant.isShow &&
      !this.config.isSpanControl &&
      !this.config.isHomeSite

    if (canShowAiAssistant && aiAssistantCard) {
      this.setFadeVisible(aiAssistantCard, true, 'flex')
      localStorage.setItem('AIAssistantShown', String(shownCount + 1))
    } else if (
      this.config.greeting.enabled &&
      (!this.config.product?.toLowerCase()?.includes('taxbandits') || this.config.isHomeSite)
    ) {
      this.setupGreeting()
    }
  }

  private updateBadgeCount(count: number, text?: string): void {
    this.setFadeVisible(this.root?.querySelector<HTMLElement>('.bot-greeting-widget'), false, 'block')

    const badge = this.root?.querySelector<HTMLElement>('.bot-badge')
    if (count > 0 && !this.chatWindow?.classList.contains('open')) {
      if (badge) badge.textContent = count > 99 ? '99+' : String(count)
      this.setFadeVisible(badge, true, 'flex')
      if (text) this.showApplicationErrorAns(text)
    } else {
      this.setFadeVisible(badge, false, 'flex')
    }
  }

  // Toggles an element's '.show' class (which every fade-capable rule in styles.ts
  // keys its opacity/transform/visibility transition off of) and, for elements that
  // stay in normal document flow (the AI-assistant card, greeting bubble) rather than
  // being position:fixed/absolute overlays, also flips `display` around the
  // transition — set immediately when showing (so the fade-in has something to
  // animate), removed only after FADE_DURATION_MS when hiding (so the fade-out has
  // time to actually play before the element stops being laid out). The guard in the
  // hide branch's timeout drops a stale hide if the element was re-shown in the
  // meantime.
  private setFadeVisible(el: HTMLElement | null | undefined, show: boolean, displayValue: string): void {
    if (!el) return
    if (show) {
      el.style.display = displayValue
      // Forces layout so the browser registers 'display' before '.show' is added —
      // without this the two changes can coalesce into a single frame and the
      // opacity/transform transition never plays.
      void el.offsetHeight
      el.classList.add('show')
    } else {
      el.classList.remove('show')
      window.setTimeout(() => {
        if (!el.classList.contains('show')) el.style.display = 'none'
      }, FADE_DURATION_MS)
    }
  }

  // AIChatLogin failed inside the iframe: close whatever is open (window or mini chat),
  // drop any queued open, and keep the launcher hidden until a later BotReady.
  private handleAuthFailed(): void {
    this.isAuthenticated = false
    this.pendingOpen = false
    if (this.miniChatOpen) this.openMiniChatWindow({ type: 'close' })
    this.chatWindow?.classList.remove('maxmimumWidth')
    if (this.chatWindow?.classList.contains('open')) {
      this.chatWindow.classList.remove('open')
      this.syncBodyPadding()
      this.syncFloatingCloseButton()
    }
    this.syncLauncherVisibility()
  }

  private toggleBotIconVisible(isShow: boolean): void {
    if (!this.config.isShowLauncherIcon) return
    this.isLauncherManuallyHidden = !isShow
    if (!isShow) {
      this.chatWindow?.classList.remove('maxmimumWidth')
      if (this.chatWindow?.classList.contains('open')) {
        this.chatWindow.classList.toggle('open')
        this.syncBodyPadding()
        this.syncFloatingCloseButton()
      }
    }
    this.syncLauncherVisibility()
  }

  private dismissAiAssistantCard(card: HTMLElement | null | undefined): void {
    this.setFadeVisible(card, false, 'flex')
    this.sendIFrameMessage({ type: 'AIAssistantCardUpdate', message: true })
  }

  private showError(message: string): void {
    const errorDiv = this.root?.querySelector<HTMLElement>('.bot-error-message')
    if (!errorDiv) return
    errorDiv.textContent = message
    errorDiv.classList.add('show')
    setTimeout(() => {
      errorDiv.classList.remove('show')
    }, 5000)
  }

  private showApplicationErrorAns(message: string): void {
    const container = this.root?.querySelector<HTMLElement>('.application-error-ans')
    const errorAns = this.root?.querySelector<HTMLElement>('.error-ans')
    if (!errorAns) return
    const firstLine = message.split('\n')[0]?.replace(':', '')
    errorAns.innerHTML = `<div>${firstLine}... <a class="viewmore" href="javascript:void(0)">More help</a><div>`
    container?.classList.add('show')

    container?.querySelector('.viewmore')?.addEventListener('click', () => {
      this.sendIFrameMessage({ type: 'OpenAIChat' })
      // open(), not the openChatWidget() toggle: the iframe answers OpenAIChat with
      // its own OpenChatBot, and a toggle landing after it would close the window.
      this.open()
    })
    container?.querySelector('.close-icon')?.addEventListener('click', () => {
      container.classList.remove('show')
    })
  }

  private addFrameAncestors(): void {
    document.getElementById(MAIN_IFRAME_ID)?.setAttribute('csp', "frame-ancestors 'none")
  }

  /** Referer allowlist check — kept for callers still exercising it, e.g. via UnAuthorized handling upstream. */
  checkValidReferer(): boolean {
    if (location.origin.includes('localhost')) return true
    return ALLOWED_DOMAINS.includes(location.origin.split('.')[1])
  }

  private captureDom(): void {
    const script = document.createElement('script')
    script.async = true
    script.src = 'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js'
    document.body.appendChild(script)
    script.onload = () => {
      void this.runCapture()
    }
  }

  private async runCapture(): Promise<void> {
    const widgetRoot = document.getElementById(WIDGET_ROOT_ID)
    // html2canvas is loaded from a CDN <script> at capture time (see captureDom()
    // above), not bundled — it isn't a package dependency, just a window global.
    type Html2Canvas = (el: HTMLElement, opts?: Record<string, unknown>) => Promise<HTMLCanvasElement>
    const html2canvas = (window as unknown as { html2canvas: Html2Canvas }).html2canvas
    if (widgetRoot) widgetRoot.style.display = 'none'
    try {
      const canvas = await html2canvas(document.body, { scrollY: -window.scrollY, useCORS: true })
      const dataUrl = canvas.toDataURL('image/png')
      this.sendIFrameMessage({ type: 'Base64Image', message: dataUrl })
    } catch (error) {
      console.error('Error capturing the document:', error)
    } finally {
      if (widgetRoot) widgetRoot.style.display = 'block'
    }
  }

  private setupGreeting(): void {
    const greeting = this.root?.querySelector<HTMLElement>('.bot-greeting-widget')
    if (!greeting) return
    const closeBtn = greeting.querySelector('.bot-greeting-close')
    const quickQuestions = greeting.querySelectorAll<HTMLButtonElement>('.bot-quick-question')

    closeBtn?.addEventListener('click', () => {
      this.setFadeVisible(greeting, false, 'block')
      localStorage.setItem('isLauncherClosed', 'true')
    })

    const askFromGreeting = (message: string | undefined) => {
      const isLauncherEnabled = localStorage.getItem('isLauncherEnabled')
      if (isLauncherEnabled === 'false' || isLauncherEnabled === null) {
        this.setFadeVisible(greeting, false, 'block')
        localStorage.setItem('isLauncherEnabled', 'true')
        this.sendIFrameMessage({ type: 'OpenAIChat', message })
        // open(), not the toggle — the iframe's own OpenChatBot reply to OpenAIChat
        // may already have opened the window within these 200ms.
        setTimeout(() => this.open(), 200)
      }
    }

    quickQuestions.forEach((button) => {
      button.addEventListener('click', () => askFromGreeting(button.dataset.question))
    })

    // "Or type your question" — sends whatever the visitor typed, same path as a quick question.
    const askForm = greeting.querySelector<HTMLFormElement>('.bot-greeting-ask')
    const askInput = askForm?.querySelector<HTMLInputElement>('.bot-greeting-ask-input')
    const askSend = askForm?.querySelector<HTMLButtonElement>('.bot-greeting-ask-send')
    askInput?.addEventListener('input', () => {
      if (askSend) askSend.disabled = !askInput.value.trim()
    })
    askForm?.addEventListener('submit', (event) => {
      event.preventDefault()
      const question = askInput?.value.trim()
      if (!question) return
      askFromGreeting(question)
    })

    const isLauncherEnabled = localStorage.getItem('isLauncherEnabled')
    const isLauncherClosed = localStorage.getItem('isLauncherClosed')
    setTimeout(() => {
      const shouldShow =
        (isLauncherEnabled === null || isLauncherEnabled === 'false') &&
        !this.chatWindow?.classList.contains('open') &&
        this.config.authStatus.toUpperCase() === 'AUTHENTICATED' &&
        (isLauncherClosed === null || isLauncherClosed === 'false')
      if (!shouldShow) return
      this.setFadeVisible(greeting, true, 'block')
      // Intro sweep of the input's gradient ring for the first 5s, same as the
      // chat composer's; :focus-within shows it again on focus independently.
      askForm?.classList.add('intro')
      setTimeout(() => askForm?.classList.remove('intro'), 5000)
    }, this.config.greeting.delay)

    if (this.config.greeting.autohide) {
      setTimeout(() => {
        this.setFadeVisible(greeting, false, 'block')
      }, this.config.greeting.delay + this.config.greeting.autohideDelay)
    }

    this.launcherPrimaryBtn?.addEventListener('click', () => {
      this.setFadeVisible(greeting, false, 'block')
    })
    this.launcherSecondaryBtn?.addEventListener('click', () => {
      this.setFadeVisible(greeting, false, 'block')
    })
  }
}
